import {
  can,
  DEFAULT_POLICY_SETTINGS,
  type AckResult,
  type AssistantAnswer,
  type AssistantStatus,
  type LiveActionItem,
} from '@live-stream/shared'
import type { Room } from '../room.js'
import type { AppSocket } from '../socket.js'
import { derived, patch, readonly, watch, writable, type ReadableStore } from '../store.js'
import { failureText, unseenItems } from './assistant-format.js'

/**
 * The in-meeting assistant, this browser's side: "Catch me up", questions
 * about the meeting so far, and the live action items.
 *
 * It works from the live captions, so it is there only while a host has
 * them on — the same rule the server enforces, read from the shared policy.
 * Answers are this person's alone and last for the page; the action items
 * are the room's, pushed to everyone as they change, and fetched with the
 * status on joining so a latecomer sees the list so far.
 */

/** Claude at low effort answers in seconds; this is the wait before saying it did not. */
const ANSWER_TIMEOUT_MS = 60_000

export interface AskedAnswer {
  id: number
  answer: AssistantAnswer
}

export interface AssistantState {
  open: boolean
  items: LiveActionItem[]
  /** Items that have been on screen, by id: the badge counts the rest. */
  seen: ReadonlySet<string>
  answers: AskedAnswer[]
  /** What is being answered right now. */
  thinking: 'catch-up' | 'question' | null
  pendingQuestion: string
  error: string | null
  /** The server thinks a catch-up is worth offering; until they take it or wave it off. */
  nudge: boolean
}

export class Assistant {
  private readonly store = writable<AssistantState>({
    open: false,
    items: [],
    seen: new Set(),
    answers: [],
    thinking: null,
    pendingQuestion: '',
    error: null,
    nudge: false,
  })
  readonly state: ReadableStore<AssistantState> = readonly(this.store)
  /** The server has Claude and a transcription service. */
  readonly available: ReadableStore<boolean>
  /** Usable now: available, and the policy allows it (captions on, AI on). */
  readonly usable: ReadableStore<boolean>
  /** Why it cannot be used now, for the panel; null when it can. */
  readonly reason: ReadableStore<string | null>
  /** Action items not yet seen, for the badge; 0 while the panel is open. */
  readonly unseen: ReadableStore<number>

  /** Taken or waved off: not offered again this meeting. */
  private nudgeDone = false
  private answerId = 0
  private boundTo: AppSocket | null = null

  constructor(private readonly room: Room) {
    const r = room.state
    this.available = derived([r], (s) => s.assistantAvailable)
    const verdict = derived([r], (s) =>
      can({ role: s.myRole }, 'assistant:use', {
        // The room's AI switch, as its state mirrors it.
        settings: { ...DEFAULT_POLICY_SETTINGS, aiEnabled: s.room.aiEnabled },
        captionsActive: s.room.captions.active,
        // And the host's switch for attendees.
        permissions: s.room.permissions,
      }),
    )
    this.usable = derived([this.available, verdict], (a, v) => a && v.allowed)
    this.reason = derived([this.available, verdict], (a, v) =>
      !a ? 'The assistant is not set up on this server.' : v.allowed ? null : v.reason,
    )
    this.unseen = derived([this.store], (s) => (s.open ? 0 : unseenItems(s.items, s.seen)))

    watch(
      room.socket,
      (s) => s,
      (sock) => this.bind(sock),
      { immediate: true },
    )
    // Back after being away: what was missed meanwhile may be worth a nudge again.
    watch(
      r,
      (s) => s.rejoinCount,
      () => {
        this.nudgeDone = false
      },
    )
    // On joining (and every rejoin), and whenever captions come on or go off.
    watch(
      r,
      (s) => `${s.phase}|${s.sessionId}|${s.rejoinCount}|${s.room.captions.active}`,
      (_next, prev) => {
        const phase = r.get().phase
        if (phase === 'joined') void this.refresh()
        else if (prev?.startsWith('joined|')) this.reset()
      },
      { immediate: true },
    )
  }

  private get s(): AssistantState {
    return this.store.get()
  }

  private markSeen(): void {
    patch(this.store, { seen: new Set([...this.s.seen, ...this.s.items.map((i) => i.id)]) })
  }

  private readonly onItems = (msg: { items: LiveActionItem[] }): void => {
    patch(this.store, { items: msg.items })
    if (this.s.open) this.markSeen()
  }

  private bind(sock: AppSocket | null): void {
    if (this.boundTo === sock) return
    this.boundTo?.off('assistant:action-items', this.onItems)
    this.boundTo = sock
    sock?.on('assistant:action-items', this.onItems)
  }

  private call<T>(send: (sock: AppSocket, done: (r: AckResult<T>) => void) => void) {
    return new Promise<AckResult<T>>((resolve) => {
      const sock = this.room.socket.get()
      if (!sock?.connected) {
        resolve({ ok: false, error: { code: 'offline', message: 'Not connected to the meeting.' } })
        return
      }
      const timer = setTimeout(
        () => resolve({ ok: false, error: { code: 'timeout', message: '' } }),
        ANSWER_TIMEOUT_MS,
      )
      send(sock, (r) => {
        clearTimeout(timer)
        resolve(r)
      })
    })
  }

  async refresh(): Promise<void> {
    if (this.room.state.get().phase !== 'joined' || !this.available.get()) return
    const res = await this.call<AssistantStatus>((s, done) => s.emit('assistant:status', done))
    if (!res.ok) return
    patch(this.store, { items: res.data.items, nudge: res.data.nudge && !this.nudgeDone })
    if (this.s.open) this.markSeen()
  }

  private reset(): void {
    this.nudgeDone = false
    patch(this.store, {
      open: false,
      items: [],
      seen: new Set(),
      answers: [],
      thinking: null,
      pendingQuestion: '',
      error: null,
      nudge: false,
    })
  }

  private keep(result: AckResult<AssistantAnswer>): boolean {
    if (!result.ok) {
      patch(this.store, { error: failureText(result) })
      return false
    }
    patch(this.store, {
      answers: [...this.s.answers, { id: ++this.answerId, answer: result.data }],
    })
    return true
  }

  async catchUp(): Promise<void> {
    if (this.s.thinking) return
    this.dismissNudge()
    this.setOpen(true)
    patch(this.store, { error: null, thinking: 'catch-up' })
    try {
      this.keep(await this.call<AssistantAnswer>((s, done) => s.emit('assistant:catch-up', done)))
    } finally {
      patch(this.store, { thinking: null })
    }
  }

  /** Ask a question; true when it was answered (the box may be cleared). */
  async ask(question: string): Promise<boolean> {
    const q = question.trim()
    if (!q || this.s.thinking) return false
    patch(this.store, { error: null, thinking: 'question', pendingQuestion: q })
    try {
      return this.keep(
        await this.call<AssistantAnswer>((s, done) =>
          s.emit('assistant:ask', { question: q }, done),
        ),
      )
    } finally {
      patch(this.store, { thinking: null, pendingQuestion: '' })
    }
  }

  setOpen(open: boolean): void {
    patch(this.store, { open })
    if (open) this.markSeen()
  }

  toggle(): void {
    this.setOpen(!this.s.open)
  }

  dismissNudge(): void {
    patch(this.store, { nudge: false })
    this.nudgeDone = true
  }
}
